import { useEffect, useState, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useOfflineStorage } from './useOfflineStorage';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';

export const useDataPreloader = () => {
  const { user } = useAuth();
  const { cacheData, isOnline } = useOfflineStorage();
  const { toast } = useToast();
  const [isPreloading, setIsPreloading] = useState(false);
  const [preloadProgress, setPreloadProgress] = useState(0);

  const preloadAllData = useCallback(async () => {
    if (!user || !isOnline || isPreloading) return;

    try {
      setIsPreloading(true);
      setPreloadProgress(0);

      console.log('🔄 Iniciando precarga de datos...');

      // 1. Obligaciones Ambientales (14%)
      try {
        const { data: obligaciones } = await supabase
          .from('obligaciones_ambientales')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (obligaciones) {
          await cacheData('obligaciones', 'all', obligaciones);
          console.log('✅ Obligaciones cargadas:', obligaciones.length);
        }
        setPreloadProgress(14);
      } catch (error) {
        console.error('Error cargando obligaciones:', error);
      }

      // 2. Documentos Legales (28%)
      try {
        const { data: docsLegales } = await supabase
          .from('documentos_legales')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (docsLegales) {
          await cacheData('documentos', `documentos_legales_${user.id}`, docsLegales);
          console.log('✅ Documentos legales cargados:', docsLegales.length);
        }
        setPreloadProgress(28);
      } catch (error) {
        console.error('Error cargando documentos legales:', error);
      }

      // 3. Documentos Sociales (42%)
      try {
        const { data: docsSociales } = await supabase
          .from('documentos_sociales')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
        
        if (docsSociales) {
          // Cachear todos y también por tipo
          await cacheData('documentos', `documentos_sociales_${user.id}_all`, docsSociales);
          
          // Cachear por cada tipo de documento
          const tipos = ['soporte', 'acta', 'lista_asistencia', 'foto', 'video', 'audio', 'informe', 'correspondencia_entrada', 'correspondencia_salida', 'pqrs', 'otros'];
          for (const tipo of tipos) {
            const filtered = docsSociales.filter((d: any) => d.tipo_documento === tipo);
            await cacheData('documentos', `documentos_sociales_${user.id}_${tipo}`, filtered);
          }
          console.log('✅ Documentos sociales cargados:', docsSociales.length);
        }
        setPreloadProgress(42);
      } catch (error) {
        console.error('Error cargando documentos sociales:', error);
      }

      // 4. Documentos GDB (56%)
      try {
        const { data: docsGDB } = await supabase
          .from('documentos_gdb')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (docsGDB) {
          await cacheData('documentos', `documentos_gdb_${user.id}_all`, docsGDB);
          
          // Cachear por categoría
          const categorias = [...new Set(docsGDB.map((d: any) => d.categoria))];
          for (const categoria of categorias) {
            const filtered = docsGDB.filter((d: any) => d.categoria === categoria);
            await cacheData('documentos', `documentos_gdb_${user.id}_${categoria}`, filtered);
          }
          console.log('✅ Documentos GDB cargados:', docsGDB.length);
        }
        setPreloadProgress(56);
      } catch (error) {
        console.error('Error cargando documentos GDB:', error);
      }

      // 5. Cumplimientos (70%)
      try {
        const { data: cumplimientos } = await supabase
          .from('cumplimientos_documentos')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
        
        if (cumplimientos) {
          // Cachear por documento
          const documentosIds = [...new Set(cumplimientos.map((c: any) => c.documento_id))];
          for (const docId of documentosIds) {
            const filtered = cumplimientos.filter((c: any) => c.documento_id === docId);
            await cacheData('cumplimientos', `cumplimientos_${docId}_${user.id}`, filtered);
          }
          console.log('✅ Cumplimientos cargados:', cumplimientos.length);
        }
        setPreloadProgress(70);
      } catch (error) {
        console.error('Error cargando cumplimientos:', error);
      }

      // 6. Evidencias de Cumplimiento (85%)
      try {
        const { data: evidencias } = await supabase
          .from('evidencias_cumplimiento')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (evidencias) {
          // Cachear por cumplimiento
          const cumplimientosIds = [...new Set(evidencias.map((e: any) => e.cumplimiento_id))];
          for (const cumpId of cumplimientosIds) {
            const filtered = evidencias.filter((e: any) => e.cumplimiento_id === cumpId);
            await cacheData('evidencias', `evidencias_${cumpId}`, filtered);
          }
          console.log('✅ Evidencias cargadas:', evidencias.length);
        }
        setPreloadProgress(85);
      } catch (error) {
        console.error('Error cargando evidencias:', error);
      }

      // 7. Planes de Manejo (100%)
      try {
        const { data: planes } = await supabase
          .from('planes_manejo')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (planes) {
          // Cachear por obligación
          const obligacionesIds = [...new Set(planes.map((p: any) => p.obligacion_id))];
          for (const oblId of obligacionesIds) {
            const filtered = planes.filter((p: any) => p.obligacion_id === oblId);
            await cacheData('documentos', `planes_manejo_${oblId}`, filtered);
          }
          console.log('✅ Planes de manejo cargados:', planes.length);
        }
        setPreloadProgress(100);
      } catch (error) {
        console.error('Error cargando planes de manejo:', error);
      }

      console.log('✅ Precarga completa finalizada');

    } catch (error) {
      console.error('Error en precarga de datos:', error);
      toast({
        title: "Error en precarga",
        description: "Algunos datos pueden no estar disponibles offline",
        variant: "destructive",
      });
    } finally {
      setIsPreloading(false);
      setPreloadProgress(0);
    }
  }, [user, isOnline, isPreloading, cacheData, toast]);

  // Ejecutar precarga cuando el usuario inicia sesión y hay internet
  useEffect(() => {
    if (user && isOnline && !isPreloading) {
      // Esperar 2 segundos después del login para no bloquear la UI inicial
      const timeout = setTimeout(() => {
        preloadAllData();
      }, 2000);
      
      return () => clearTimeout(timeout);
    }
  }, [user, isOnline]);

  // Re-ejecutar precarga cada 5 minutos si está online
  useEffect(() => {
    if (user && isOnline) {
      const interval = setInterval(() => {
        preloadAllData();
      }, 5 * 60 * 1000); // 5 minutos
      
      return () => clearInterval(interval);
    }
  }, [user, isOnline, preloadAllData]);

  return {
    isPreloading,
    preloadProgress,
    preloadAllData,
  };
};
